import {
    isCornerSingleSidePoi,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { buildExistingPlacements } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { EMPTY_GUID } from 'shared/constants';

import type {
    AutoFormBuildingRequest,
    AutoFormBuildingResponse
} from 'app/modules/wallforming/components/auto-form/types/autoFormTypes';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

/** Forms every wall/corner POI currently loaded (i.e. the active storey) with the user's solver settings. */
export function buildAutoFormRequest(
    state: PlacementState,
    sessionId: string,
    systemId: string
): AutoFormBuildingRequest {
    const poiIndices = state.pois
        .map(({ poi }, index) => ({ poi, index }))
        .filter(({ poi }) => poi.poiGuid !== EMPTY_GUID && (isPlanarFacePoi(poi) || isCornerSingleSidePoi(poi)));
    const { placements, connectorPlacements } = buildExistingPlacements(
        state,
        state.pois.map((_, index) => index),
        true
    );
    const panelSettings = state.automatedPlacementSettingsByType.Panels;

    return {
        sessionId,
        systemId,
        existingPlacements: placements,
        existingConnectorPlacements: connectorPlacements,
        poiIds: poiIndices.map(({ poi }) => poi.poiGuid),
        requireVerticalGap: panelSettings.requireVerticalGap ?? false,
        gapPositionPreferences: panelSettings.gapPositionPreferences ?? null,
        costProfile: { profileId: 'default', version: 'v1', weights: panelSettings.solverCostWeights },
        cornerCostWeights: state.automatedPlacementSettingsByType.Corners.solverCostWeights,
        publishPlacements: false,
        autoPlaceSupportingConstructionClamps: state.autoPlaceSupportingConstructionClamps
    };
}

/** Unwraps HTTP/API `data` envelopes (axios response and/or API envelope) around the auto-form result. */
export function unwrapAutoFormResponse(response: unknown): AutoFormBuildingResponse {
    let payload = response;
    for (let depth = 0; depth < 3 && isRecord(payload) && !('accepted' in payload) && 'data' in payload; depth += 1) {
        payload = payload.data;
    }

    if (!isRecord(payload) || typeof payload.accepted !== 'boolean' || !Array.isArray(payload.placements)) {
        throw new Error('The auto-forming response is invalid.');
    }

    return payload as unknown as AutoFormBuildingResponse;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
